import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { tap, first, filter } from 'rxjs/operators';
import { EstimateType, RevenueAreaType } from '../../../client-api.service';
import { CommentsStateService } from '../../../comments/comments-state.service';
import { MenuTrayStateService } from '../../../menu-tray/menu-tray-state.service';
import { AggregateUtil } from '../../../shared/aggregate-util';
import { GLOBAL, IGlobalSettings } from '../../../shared/global-settings';
import { tapError } from '../../../shared/tap-error-operator';
import { EstimateInputDebounceManager } from '../../estimate-input-debounce-manager';
import { AreaEstimates } from '../../models/area-estimates';
import { AreaType, ExtendedRevenueAreaType } from '../../models/area-type';
import { RevenueStateService, RootAreaState } from '../../revenue-state.service';
import { SubsManager } from 'src/app/shared/tcc-ui/subs-manager';
import { NotificationsService } from 'src/app/shared/tcc-ui/notifications';


@Component({
  selector: 'app-revenue-total',
  templateUrl: './revenue-total.component.html'
})
export class RevenueTotalComponent implements OnInit, OnDestroy {
  private static readonly fpRateLabel = 'Fp Rate';

  private areaIdMap: Map<number, AreaEstimates> | undefined;
  private areaTypeMap: Map<AreaType, AreaEstimates[]> | undefined;
  private subsMgr = new SubsManager();
  private utConsolidatedAmenityMap: Map<number, AreaEstimates[]> | undefined;

  readonly comments$ = this.revState.comments$;
  readonly cyJanPeriodIndex = this.revState.cyJanPeriodIndex;
  readonly inputDebounceMgr = new EstimateInputDebounceManager(this.revState.periodSettings);

  amenities: AreaEstimates[] | undefined;
  curArea: AreaEstimates | undefined;
  expandedAreas = new Set<string>();
  isDebugRowsVisible = false;
  isReadOnly$ = this.revState.isReadOnly$;
  periodSettings = this.revState.periodSettings;
  unitTypes: AreaEstimates[] | undefined; 

  constructor(
    private commentsState: CommentsStateService,
    @Inject(GLOBAL) private globalSettings: IGlobalSettings,
    private notifySvc: NotificationsService,
    private revState: RevenueStateService,
    private trayStateSvc: MenuTrayStateService
  ) {
  }

  ngOnInit() {
    
    this.subsMgr.addSub = this.revState.areas$.pipe(
      filter((x): x is RootAreaState => !!x),
      tap(({ idMap, typeMap, utConsolidatedAmenityMap }) => {

        this.areaIdMap = idMap;
        this.areaTypeMap = typeMap;
        this.utConsolidatedAmenityMap = utConsolidatedAmenityMap;
        this.setCurrentArea(this.getSelectedAreaOrSelf());
      })
    ).subscribe();

    this.subsMgr.addSub = this.revState.selectedIds.setChange$.pipe(
      tap(ids => {
        const area = this.getSelectedAreaOrSelf(ids);
        this.setCurrentArea(area);
      }
      )).subscribe();

    // this stream saves changes
    this.subsMgr.addSub = this.inputDebounceMgr.estimateChange$.pipe(
      tap(x => {
        this.doOccupancyAdjustments(x.value.revAreaId!, x.value.appliedOn!, x.value.value);
      }),
      tapError(() => this.notifySvc.addError('Unable to save estimates.  Please refresh and try again.'))
    ).subscribe();
  }

  ngOnDestroy() {
    this.subsMgr.onDestroy();
  }

  isExpanded(key: string): boolean {
    return this.expandedAreas.has(key);
  }

  isDetailVisible(unitType: AreaEstimates): boolean {
    return this.curArea?.areaType === RevenueAreaType.Self
      || this.curArea?.areaType === RevenueAreaType.NumberOfBedrooms
      || this.curArea?.revAreaId === unitType.revAreaId;
  }

  showComment(commentAccountSrc: 'gpr' | 'vacancyLoss') {
    this.comments$.pipe(first()).subscribe(x => {
      this.commentsState.selectedAccountCode = x[commentAccountSrc].accountCode;
      this.trayStateSvc.openTray('CommentManagement');
    });

  }

  toCamelCase(input: string): string {
    return input
      .replace(/[-_\s]+(.)?/g, (_, c) => c ? c.toUpperCase() : '')
      .replace(/^(.)/, (m) => m.toLowerCase());
  }

  toggle(key: string) {
    console.log('toggling key ', key);
    this.expandedAreas.has(key) ? this.expandedAreas.delete(key) : this.expandedAreas.add(key);
  }

  updateOccEstimate(revAreaId: number, periodIndex: number, valueRaw: string) {
    const appliedOn = this.revState.periodSettings.periods[periodIndex].value;
    
    try {
      this.inputDebounceMgr.updateRawEstimatePct(valueRaw, { revAreaId, appliedOn, estimateType: EstimateType.TargetOccupancyPct });
    }
    catch {
      this.notifySvc.addError(`Invalid value for estimate: ${valueRaw}.  Nothing was saved.`);
    }
  }

  updateOccEstimateForce(revAreaId: number, periodIndex: number) {
    const appliedOn = this.revState.periodSettings.periods[periodIndex].value;
    this.inputDebounceMgr.updateForce({ revAreaId, appliedOn });
  }

  private doOccupancyAdjustments(revAreaId: number, appliedOn: Date, newOccupancyPct: number) {
    const periodIndex = this.revState.periodSettings.datePeriodIndexMap.get(appliedOn.valueOf()) || 0;
    const unitTypes = this.getUnitTypeAreas(revAreaId);

    const unitCount = AggregateUtil.sum(unitTypes.map(x => x.periods[periodIndex].unitCount) || []);
    const renewalLeases = AggregateUtil.sum(unitTypes.map(x => x.periods[periodIndex].estRenewalLeases) || []);
    const newLeases = AggregateUtil.sum(unitTypes.map(x => x.periods[periodIndex].estNewLeases) || []);
    const totalLeases = renewalLeases + newLeases;
    const curOccupancy = totalLeases / unitCount;
    const occupancyChange = newOccupancyPct - curOccupancy;
    if (occupancyChange === 0) {
      return;
    }
    const orgArea = this.areaTypeMap?.get(RevenueAreaType.Self)?.[0];
    if (orgArea == null) {
      throw new Error('Top level area was not found.');
    }
    const utChanges = unitTypes.map(x => ({ area: x, leasesDelta: 0, boundsReached: false }));
    
    // keeps track of changes adjustment by adjustment;
    let remainingUnitsToAdjust = occupancyChange * unitCount;
    // it's hard to land on 0.  As long as the number is within an acceptable tolerance then stop looping.
    while (remainingUnitsToAdjust > 0.0001 || remainingUnitsToAdjust < -.0001) {
      const unitsToAdjustAtLoopStart = remainingUnitsToAdjust;
      // keep looping, proportionately assigning excess until all bounds have been met or all unit adjustments assigned
      const openUnitTypes = utChanges?.filter(x => !x.boundsReached);
      if (openUnitTypes?.length === 0) {
        console.warn('Reached end of assinging units without exhausting expected adjustment amount.');
        break;
      }
      for (const ut of openUnitTypes) {
          const estimates = ut.area.periods[periodIndex];
          const unitTypeUnitCount = estimates.unitCount || 0;
          const utTotalLeases = (estimates.estNewLeases || 0) + (estimates.estRenewalLeases || 0) + ut.leasesDelta;
          const sizeFactor = unitTypeUnitCount / unitCount; // use this to get proportional adjustment amount.
          let leasesDelta = sizeFactor * unitsToAdjustAtLoopStart;

          if (utTotalLeases + leasesDelta <= 0) {
            leasesDelta -= utTotalLeases + leasesDelta;
            ut.boundsReached = true;
          }
          else if (utTotalLeases + leasesDelta >= unitTypeUnitCount) {
            leasesDelta += unitTypeUnitCount - (utTotalLeases + leasesDelta);
            ut.boundsReached = true;
          }
          ut.leasesDelta += leasesDelta;
          remainingUnitsToAdjust -= leasesDelta;
      }
    }

    for (const ut of utChanges.filter(x => x.leasesDelta !== 0)) {
        const estimates = ut.area.periods[periodIndex];
        const estNewLeases = estimates.estNewLeases || 0;
        const estRenewalLeases = estimates.estRenewalLeases || 0;
        const tgtRenewalPct = orgArea.periods[this.periodSettings.cyJan.index].tgtRenewalPct || 0;
        const tgtOccPct = orgArea.periods[this.periodSettings.cyJan.index].tgtOccPct || 0;
        // determine the newToOverallOccupancy ratio for the unit type.  If there are no estimates, use the targets.
        const newRenewalRatio = (estNewLeases || estRenewalLeases)
          ? estNewLeases / (estNewLeases + estRenewalLeases)
          : (tgtOccPct) ? (1 - tgtRenewalPct / tgtOccPct) : 0;
        const utEstNewLeases = estNewLeases + newRenewalRatio * ut.leasesDelta;
        const utEstRenewalLeases = estRenewalLeases + (1 - newRenewalRatio) * ut.leasesDelta;
        this.revState.enqueueEstimateSave(ut.area.revAreaId, {
          appliedOn: appliedOn,
          estimateId: 0,
          estimateType: EstimateType.EstNewLeases,
          value: utEstNewLeases
        });
        this.revState.enqueueEstimateSave(ut.area.revAreaId, {
          appliedOn: appliedOn,
          estimateId: 0,
          estimateType: EstimateType.EstRenewalLeases,
          value: utEstRenewalLeases
        });
    }
  }
  /**
   * Gets an array of UnitTypes from an Org area's children, or returns an array of just the revArea if the id past is a unit type's.
   * Otherwise returns an empty array.
   */
  private getUnitTypeAreas(revAreaId: number) {
    const revArea = this.areaIdMap?.get(revAreaId);
    if (revArea?.areaType === RevenueAreaType.Self) {
      return revArea.children || [];
    }
    else if (revArea?.areaType === RevenueAreaType.NumberOfBedrooms) {
      return revArea.children || [];
    }
    else if (revArea?.areaType === RevenueAreaType.UnitType) {
      return [revArea];
    }
    return [];
  }

  private getSelectedAreaOrSelf(ids = this.revState.selectedIds.items) {
    const selectedArea = ids.length > 0
      ? this.areaIdMap?.get(ids[0])
      : undefined;
    return selectedArea || this.areaTypeMap!.get(RevenueAreaType.Self)![0];
  }

  private setCurrentArea(area: AreaEstimates) {
    this.curArea = area;

    if (!area) {
      this.unitTypes = [];
      this.amenities = [];
      return;
    }
    else if (area.areaType === RevenueAreaType.Self) {
      
      this.unitTypes = area.children?.filter(ut => ut.areaType === RevenueAreaType.UnitType);
      this.amenities = this.utConsolidatedAmenityMap?.get(area.revAreaId) || [];      
    }
    else if (area.areaType === RevenueAreaType.NumberOfBedrooms) {
      this.unitTypes = area.children?.filter(ut => ut.areaType === RevenueAreaType.UnitType);
      this.amenities = this.createNumberOfBedroomsAmenities(area);
    }
    else if (area.areaType === RevenueAreaType.UnitType) {
      this.unitTypes = [area];
      this.amenities = (this.utConsolidatedAmenityMap?.get(area.revAreaId) || [])
        .map(amenity => this.withAmenityBadge(
          amenity,
          amenity.summary!.unitCount !== area.summary!.unitCount! ? amenity.summary!.unitCount!.toString() : ''
        ));
    }
  }

  private createNumberOfBedroomsAmenities(numberOfBedroomsArea: AreaEstimates) {
    const amenitiesByName = new Map<string, AreaEstimates>();

    for (const unitType of numberOfBedroomsArea.children || []) {
      for (const unitTypeAmenity of this.utConsolidatedAmenityMap?.get(unitType.revAreaId) || []) {
        const amenityName = this.getAmenityNameWithoutBadge(unitTypeAmenity.name);
        let numberOfBedroomsAmenity = amenitiesByName.get(amenityName);
        if (!numberOfBedroomsAmenity) {
          numberOfBedroomsAmenity = {
            areaType: ExtendedRevenueAreaType.ConsolidatedUnitTypeAmenity,
            children: [],
            displayName: unitTypeAmenity.displayName,
            name: amenityName,
            parentRevAreaId: numberOfBedroomsArea.revAreaId,
            periods: this.periodSettings.periods.map((_, i) => ({ monthId: i, aggs: {} })),
            revAreaId: numberOfBedroomsArea.revAreaId * 16777216 + unitTypeAmenity.revAreaId,
            summary: { monthId: -1, aggs: {} }
          };
          amenitiesByName.set(amenityName, numberOfBedroomsAmenity);
        }

        numberOfBedroomsAmenity.children?.push(...unitTypeAmenity.children || []);
      }
    }

    const amenities = Array.from(amenitiesByName.values())
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    for (const amenity of amenities) {
      this.setNumberOfBedroomsAmenityCalcs(amenity);
      const badgeInfo = amenity.summary!.unitCount !== numberOfBedroomsArea.summary!.unitCount
        ? amenity.summary!.unitCount!.toString()
        : '';
      amenity.name = this.getAmenityNameWithBadge(amenity.name, badgeInfo);
    }

    return amenities;
  }

  private withAmenityBadge(amenity: AreaEstimates, badgeInfo: string) {
    return {
      ...amenity,
      name: this.getAmenityNameWithBadge(amenity.name, badgeInfo)
    };
  }

  private getAmenityNameWithBadge(name: string, badgeInfo: string) {
    return `${this.getAmenityNameWithoutBadge(name)}~~${badgeInfo}`;
  }

  private getAmenityNameWithoutBadge(name: string) {
    return name.split('~~')[0];
  }

  private setNumberOfBedroomsAmenityCalcs(amenity: AreaEstimates) {
    for (let periodIdx = 0; periodIdx < this.periodSettings.periods.length; periodIdx++) {
      const childPeriodEstimates = amenity.children?.map(x => x.periods[periodIdx]) || [];
      amenity.periods[periodIdx].estNewRate = AggregateUtil.avg(childPeriodEstimates.map(x => x.estNewRate), { skipBlanks: true });
      amenity.periods[periodIdx].origFpRate = AggregateUtil.avg(childPeriodEstimates.map(x => x.origFpRate), { skipBlanks: true });
      amenity.periods[periodIdx].unitCount = childPeriodEstimates.length;
    }

    const cyPeriods = amenity.periods.slice(this.periodSettings.cyJan.index);
    amenity.summary = {
      ...amenity.summary!,
      estNewRate: AggregateUtil.avg(cyPeriods.map(x => x.estNewRate), { skipBlanks: true }),
      origFpRate: AggregateUtil.avg(cyPeriods.map(x => x.origFpRate), { skipBlanks: true }),
      unitCount: AggregateUtil.avg(cyPeriods.map(x => x.unitCount), { skipBlanks: true })
    };
  }
}
